export enum AppRuntimeType {
  Electron = 'electron',
  Web = 'web'
}

/** Base shape of the Electron preload API (window.electronAPI) without schema generics. */
export interface ElectronApiBase {
  invoke(channel: string, request?: unknown): Promise<unknown>;
  off(event: string, callback: (...args: unknown[]) => void): void;
  on(event: string, callback: (...args: unknown[]) => void): void;
}

export interface IAppRuntime {
  getElectronApi: <T = ElectronApiBase>() => T | undefined;
  getRuntime: () => AppRuntimeType;
  getSystemThemePreference: () => 'dark' | 'light';
  isElectron: () => boolean;
  isLinux: () => boolean;
  isMacOS: () => boolean;
  isWindows: () => boolean;
}

/**
 * AppRuntime answers "What Platform/Runtime am I running on?"
 * It provides utility functions to detect Electron vs Web and OS-level platform (Windows, macOS, Linux).
 */
export const AppRuntime: IAppRuntime = {
  /**
   * Returns the Electron preload API (window.electronAPI) cast to T, or undefined if not in Electron.
   */
  getElectronApi: <T = ElectronApiBase>(): T | undefined => {
    if (!AppRuntime.isElectron()) return undefined;
    const win = typeof globalThis.window !== 'undefined' ? (globalThis.window as any) : undefined;
    return win?.electronAPI as T | undefined;
  },
  /**
   * Returns a friendly runtime type: AppRuntimeType.Electron or AppRuntimeType.Web.
   */
  getRuntime: (): AppRuntimeType => {
    return AppRuntime.isElectron() ? AppRuntimeType.Electron : AppRuntimeType.Web;
  },
  /**
   * Returns the OS-level color scheme preference ('dark' or 'light').
   * Falls back to 'light' when `window` or `matchMedia` is unavailable (eg: SSR).
   * TODO: Does this belong here?
   */
  getSystemThemePreference: (): 'dark' | 'light' => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  },
  /**
   * Returns true when running inside an Electron renderer process.
   * Relies on the Electron preload script exposing window.electronAPI via contextBridge.
   * See: modules/electron/src/preload/PreloadWrapperAlpha.ts
   */
  isElectron: (): boolean => {
    try {
      const win = typeof globalThis.window !== 'undefined' ? (globalThis.window as any) : undefined;
      return !!(win && win.electronAPI);
    } catch {
      return false;
    }
  },
  /** Returns true when running on Linux (neither macOS nor Windows). */
  isLinux: (): boolean => {
    return !AppRuntime.isMacOS() && !AppRuntime.isWindows();
  },
  /**
   * Returns true when running on macOS. Uses the modern `userAgentData` API
   * with a fallback to the deprecated `navigator.platform`.
   */
  isMacOS: (): boolean => {
    return _getNormalizedPlatform().startsWith('mac') || _getNormalizedPlatform() === 'macos';
  },
  /**
   * Returns true when running on Windows.
   */
  isWindows: (): boolean => {
    return _getNormalizedPlatform().startsWith('win');
  }
};

const _getNormalizedPlatform = (): string => {
  if (typeof navigator === 'undefined') return '';
  const raw = (navigator as any).userAgentData?.platform ?? navigator.platform ?? '';
  return raw.toLowerCase();
};
